import React, { useState } from 'react';
import type { Venue, MenuType, MenuItem } from '../../../types/venue';
import { updateMenu, uploadMenuPdf } from '../../../utils/venueApi';
import Button from '../../ui/Button';

interface MenuSettingsPanelProps {
  venue: Venue;
  onUpdate: (venue: Venue) => void;
}

const inputBase =
  'w-full px-3 py-2.5 border border-coffee-200 rounded-lg text-sm text-coffee-900 bg-white focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:border-coffee-400 transition-colors';

const MenuSettingsPanel: React.FC<MenuSettingsPanelProps> = ({ venue, onUpdate }) => {
  const [menuType, setMenuType] = useState<MenuType>(venue.menu?.type ?? 'url');
  const [externalUrl, setExternalUrl] = useState(venue.menu?.externalUrl ?? '');
  const [items, setItems] = useState<MenuItem[]>(venue.menu?.items ?? []);
  const [newItem, setNewItem] = useState({ category: '', name: '', price: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const menuData: any = { type: menuType };
      if (menuType === 'url') menuData.externalUrl = externalUrl;
      if (menuType === 'built') menuData.items = items;
      const updated = await updateMenu(venue.id, menuData);
      onUpdate(updated);
      setFeedback({ type: 'success', message: 'Menu settings saved.' });
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setFeedback(null);
    try {
      await uploadMenuPdf(venue.id, file);
      setMenuType('pdf');
      setFeedback({ type: 'success', message: 'Menu PDF uploaded.' });
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message || 'Upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  const addItem = () => {
    if (!newItem.name.trim()) return;
    setItems([
      ...items,
      {
        id: `item-${Date.now()}`,
        category: newItem.category,
        name: newItem.name,
        price: parseFloat(newItem.price) || 0,
        description: newItem.description || undefined,
      },
    ]);
    setNewItem({ category: '', name: '', price: '', description: '' });
  };

  const removeItem = (id: string) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const MENU_TYPES: { value: MenuType; label: string; description: string }[] = [
    { value: 'url', label: 'External URL', description: 'Link to your existing online menu' },
    { value: 'pdf', label: 'PDF upload', description: 'Upload a PDF version of your menu' },
    { value: 'built', label: 'Built-in menu', description: 'Build your menu with categories and items' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-1.5">Menu</div>
        <h2 className="text-display text-2xl text-coffee-900">Menu settings</h2>
      </div>

      {/* Menu type selector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {MENU_TYPES.map((mt) => (
          <button
            key={mt.value}
            onClick={() => setMenuType(mt.value)}
            className={`p-4 rounded-xl border text-left transition-colors ${
              menuType === mt.value
                ? 'border-coffee-300 bg-coffee-50'
                : 'border-coffee-100 bg-white hover:border-coffee-200'
            }`}
          >
            <p className="text-sm font-semibold text-coffee-900">{mt.label}</p>
            <p className="text-xs text-coffee-500 mt-0.5 leading-relaxed">{mt.description}</p>
          </button>
        ))}
      </div>

      {/* URL input */}
      {menuType === 'url' && (
        <div>
          <label htmlFor="menu-url" className="block text-sm font-medium text-coffee-800 mb-1.5">
            Menu URL
          </label>
          <input
            id="menu-url"
            type="url"
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
            placeholder="https://your-menu-link.com"
            className={inputBase}
          />
        </div>
      )}

      {/* PDF upload */}
      {menuType === 'pdf' && (
        <div>
          <label
            htmlFor="menu-pdf-upload"
            className="flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-coffee-200 rounded-xl text-sm text-coffee-600 hover:border-coffee-300 cursor-pointer transition-colors"
          >
            {uploading ? 'Uploading...' : 'Choose PDF file'}
          </label>
          <input
            id="menu-pdf-upload"
            type="file"
            accept=".pdf"
            onChange={handlePdfUpload}
            disabled={uploading}
            className="hidden"
          />
          {venue.menu?.pdfPath && (
            <p className="text-xs text-coffee-500 mt-1.5 font-mono">
              Current: {venue.menu.pdfPath}
            </p>
          )}
        </div>
      )}

      {/* Built-in menu editor */}
      {menuType === 'built' && (
        <div className="space-y-3">
          {items.length > 0 && (
            <div className="bg-white border border-coffee-100 rounded-2xl divide-y divide-coffee-50">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between px-5 py-3.5"
                >
                  <div>
                    <p className="text-sm font-medium text-coffee-900">{item.name}</p>
                    <p className="text-xs text-coffee-500 font-mono">
                      {item.category && `${item.category} · `}${item.price.toFixed(2)}
                    </p>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-xs text-red-500 hover:text-red-700 font-medium ml-4"
                    aria-label={`Remove ${item.name}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add item form */}
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              value={newItem.category}
              onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
              placeholder="Category"
              className={inputBase}
            />
            <input
              type="text"
              value={newItem.name}
              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              placeholder="Item name"
              className={inputBase}
            />
            <input
              type="number"
              step="0.01"
              value={newItem.price}
              onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
              placeholder="Price"
              className={inputBase}
            />
            <button
              onClick={addItem}
              disabled={!newItem.name.trim()}
              className="px-3 py-2.5 bg-coffee-50 border border-coffee-200 text-coffee-700 text-sm font-medium rounded-lg hover:bg-coffee-100 disabled:opacity-50 transition-colors"
            >
              + Add item
            </button>
          </div>
        </div>
      )}

      {feedback && (
        <div className={`text-sm rounded-xl p-4 border ${
          feedback.type === 'success'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-red-50 text-red-700 border-red-200'
        }`}>
          {feedback.message}
        </div>
      )}

      <Button variant="primary" onClick={handleSave} loading={saving}>
        {saving ? 'Saving...' : 'Save menu'}
      </Button>
    </div>
  );
};

export default MenuSettingsPanel;
